<template>
  <transition
    name="zm-zoom-in-top"
    @after-enter="handleEnter"
    @after-leave="handleLeave"
  >
    <div
      class="zm-table-column-panel"
      v-clickoutside="handleOutsideClick"
      v-show="showPopper"
      role="application"
    >
      <div class="zm-table-column-panel__group" role="dialog">
        <div class="hearder-warp" v-if="searchVisible && !movable">
          <zm-input
            v-model="filterText"
            class="filter-text"
            size="small"
            :label="searchPlaceholder"
            :placeholder="searchPlaceholder"
            :clearable="true"
            prefix-icon="zm-icon-search"
          />
        </div>
        <div
          class="zm-table-column-panel__group-list"
          :class="{
            'zm-table-column-panel__group-list-movable': movable,
            'zm-table-column-panel__scroll-height': searchVisible && !movable
          }"
        >
          <zm-scrollbar
            wrap-class="zm-table-column-panel__group-list-wrap"
          >
            <template v-if="movable">
              <draggable
                tag="zm-checkbox-group"
                :component-data="getComponentData()"
                :options="dragOptions"
                v-model="movableList"
              >
                <!-- eslint-disable vue/require-v-for-key -->
                <div
                  v-for="row in movableList"
                  tabindex="0"
                  @keydown="(e)=>{handleListKeyDown(e, row)}"
                  class="zm-table-column-panel__group-item"
                >
                  <i :class="getRowIconCls(row)"></i>
                  <zm-checkbox
                    :label="row.label"
                    :aria-label="row.label"
                    :disabled="row.disabled"
                    :name="row.displayName"
                    ref="checkbox"
                  >
                    {{ row.displayName || row.label }}
                  </zm-checkbox>
                </div>
                <!-- eslint-enable -->
              </draggable>
            </template>
            <template v-else>
              <zm-checkbox-list
                v-model="displayedProps"
                :items="screenOptions"
                :column-count="columnCount"
                :span="span"
                ref="checkboxList"
                stop-key-board-event
              >
                <template slot-scope="props">
                  <span>{{ props.item.displayName }}</span>
                  <zm-tooltip
                    v-if="props.item.tip" :content="props.item.tip.content"
                    :placement="props.item.tip.placement || 'top'"
                  >
                    <zm-button type="icon" ghost icon="zm-icon-info-outline" :aria-label="props.item.tip.ariaLabel" />
                  </zm-tooltip>
                </template>
              </zm-checkbox-list>
            </template>
          </zm-scrollbar>
          <div class="zm-table-column-panel__link" v-if="link">
            <a :href="link.href">
              <span class="zm-icon-add mgr-sm"></span>
              {{ link.text }}
            </a>
          </div>
        </div>
        <div class="pdl-md pdr-md">
          <zm-button
            type="primary"
            class="mgt-md"
            @click="handleConfirm"
            size="mini"
            :disabled="displayedProps && displayedProps.length === 0"
            aria-label="Confirm Filters"
          >
            {{ t('table.confirmFilter') }}
          </zm-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import Draggable from 'vuedraggable';
  import Popper from '../../utils/vue-popper';
  // import { PopupManager } from '../../utils/popup';
  import Locale from '../../mixins/locale';
  import Clickoutside from '../../utils/clickoutside';
  import Dropdown from './dropdown';
  import ZmCheckboxList from '../checkbox-list';
  import ZmButton from '../button';
  import ZmScrollbar from '../scrollbar';
  import AriaDialog from '../../utils/aria-dialog';
  import AriaUtils from '../../utils/aria-utils';
  import ZmCheckbox from '../checkbox';
  import zmInput from '../input';

  const EVENT_CONFIRM = 'confirm';

  export default {
    name: 'ZmTableColumnPanel',

    mixins: [Popper, Locale],

    components: {
      Draggable,
      ZmCheckbox,
      ZmCheckboxList,
      ZmButton,
      ZmScrollbar,
      zmInput
    },

    directives: {
      Clickoutside
    },

    props: {
      placement: {
        type: String,
        default: 'bottom-end'
      }
    },

    data() {
      return {
        table: null,
        cell: null,
        displayedProps: null,
        ignore: null,
        tip: null,
        count: null,
        movable: false,
        movableList: null,
        shouldTraverse: false,
        dragOptions: {
          animation: 150,
          ghostClass: 'ghost-background-class'
        },
        filterText: '',
        searchVisible: false,
        searchPlaceholder: ''
      };
    },

    computed: {
      columns() {
        return this.columnList();
      },
      link() {
        return this.table.initDisplayedColumn.link;
      },
      columnCount() {
        return this.count ? this.count : 1;
      },
      span() {
        return 24 / this.columnCount;
      },
      screenOptions() {
        const options = this.getFilteredOptions();
        return options;
      }
    },

    mounted() {
      this.popperElm = this.$el;
      this.referenceElm = this.cell;
    },

    methods: {
      handleConfirm() {
        this.$emit(EVENT_CONFIRM, this.displayedProps, this.movableList);
        this.handleOutsideClick();
      },

      handleOutsideClick() {
        setTimeout(() => {
          this.showPopper = false;
        }, 16);
      },

      handleEnter() {
        document.body.addEventListener('keydown', this.handleKeydown);
        if (this.$refs.checkboxList) {
          this.$refs.checkboxList.reset();
        }
        const focusAfterClosed = document.activeElement;
        this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed);
      },

      handleLeave() {
        document.body.removeEventListener('keydown', this.handleKeydown);
        this.ariaDialog && this.ariaDialog.closeDialog();
      },

      handleKeydown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        // const checkboxList = this.$refs.checkboxList;
        switch (keyCode) {
          case keys.esc:
            this.handleOutsideClick();
            break;
        }
      },

      getComponentData() {
        return {
          on: {
            input: this.inputChanged
          },
          props: {
            value: this.displayedProps
          }
        };
      },

      inputChanged(value) {
        this.displayedProps = value;
      },

      columnList() {
        const ignore = this.ignore;
        const tip = this.tip;
        return this.table.store.states._columns.filter((column) => {
          return column.type !== 'selection' && column.type !== 'expand' && column.type !== 'index' && !column.fixed && (!ignore || ignore.indexOf(column.property) === -1);
        }).map((column) => {
          return {
            displayName: column.label,
            label: column.property,
            ariaLabel: column.label,
            tip: tip ? tip[column.property] : false
          };
        });
      },

      getRowIconCls(row) {
        return this.shouldTraverse ? 'zm-icon-ok' : 'zm-icon-dnd';
      },

      handleListKeyDown(e, row) {
        if (e.target.className.indexOf('zm-table-column-panel__group-item') === -1) {
          return;
        }
        const movableList = this.movableList;
        const index = movableList.indexOf(row);
        if (index === -1) {
          return;
        }
        const len = movableList.length;
        const shouldTraverse = this.shouldTraverse;
        const keys = AriaUtils.keys;
        const keyCode = e.keyCode;
        switch (keyCode) {
          case keys.up:
            if (index > 0) {
              if (shouldTraverse) {
                this.swap(movableList, index, index - 1);
                this.$forceUpdate();
              }
              this.$nextTick(() => {
                if (e.target.previousSibling) {
                  e.target.previousSibling.focus();
                }
              });
              e.preventDefault();
            }
            break;
          case keys.down:
            if (index < len - 1) {
              if (shouldTraverse) {
                this.swap(movableList, index, index + 1);
                this.$forceUpdate();
              }
              this.$nextTick(() => {
                if (e.target.nextSibling) {
                  e.target.nextSibling.focus();
                }
              });
              e.preventDefault();
            }
            break;
          case keys.enter:
            this.shouldTraverse = !shouldTraverse;
            break;
        }
      },

      swap(arr, i, j) {
        const temp = arr[i];
        arr[i] = arr[j];
        arr[j] = temp;
      },

      getFilteredOptions() {
        const filterText = (this.filterText || '').trim().toLowerCase();
        if (filterText.length === 0) {
          return this.columns;
        }
        return this.columns.filter(item => {
          const label = (item['displayName'] || '').toLowerCase();
          return label.indexOf(filterText) > -1;
        });
      }
    },

    watch: {
      showPopper(val) {
        if (val) {
          Dropdown.open(this);
          this.closeBySwapedColumn = null;
          this.movableList = this.columnList();
        } else {
          Dropdown.close(this);
        }
      },
      cell(newVal) {
        this.updatePopperReference(newVal);
      }
    }
  };
</script>

<style lang="scss">
  @import "./panel-style";
</style>
